import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Check, Circle, ShieldCheck, Sparkles, X, ArrowUpRight } from "lucide-react";

/**
 * 3-step onboarding tracker shown on the Dashboard until each step is done
 * OR the athlete dismisses it. Dismissal is sticky per-user in localStorage.
 *
 * Steps:
 *   1. Create account (always complete if they see this)
 *   2. Complete athlete profile (sport + audience_size + at least one social handle)
 *   3. Verify your audience analytics by connecting Instagram (or YouTube)
 *
 * Step 3 is aspirational — it never blocks. Personal-account athletes who
 * can't OAuth see a "Switch to Creator" learn-more inline instead.
 */
const DISMISS_KEY = "pitchd_onboarding_dismissed";


function readProfile() {
  return Promise.all([
    api.get("/profile").catch(() => ({ data: null })),
    api.get("/social-connections").catch(() => ({ data: { connections: [], oauth_enabled: {} } })),
  ]);
}


export default function OnboardingProgress() {
  const [profile, setProfile] = useState(null);
  const [conns, setConns] = useState([]);
  const [oauthEnabled, setOauthEnabled] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");

  useEffect(() => {
    readProfile().then(([p, s]) => {
      setProfile(p.data || {});
      setConns(s.data?.connections || []);
      setOauthEnabled(s.data?.oauth_enabled || {});
    }).finally(() => setLoaded(true));
  }, []);

  if (!loaded || dismissed) return null;

  // Compute step states
  const hasProfile = !!(profile?.sport && profile?.audience_size &&
    (profile?.instagram_url || profile?.tiktok_url || profile?.youtube_url));
  const hasConnection = conns.length > 0;
  const hasVerified = conns.some(c => c.source === "oauth" && c.status === "connected");

  const steps = [
    { id: "signup", label: "Create your account", done: true,
      cta: null },
    { id: "profile", label: "Complete your athlete profile", done: hasProfile,
      cta: { to: "/app/profile", label: hasProfile ? "Edit profile" : "Finish profile" } },
    { id: "verify", label: "Verify your audience analytics",
      done: hasVerified, optional: true,
      cta: { to: "/app/settings", label: hasConnection ? "Manage connections" : "Connect Instagram" } },
  ];
  const completedCount = steps.filter(s => s.done).length;
  const allDone = completedCount === steps.length;
  if (allDone) return null; // hide once all 3 are done — no need to dismiss

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="surface p-6 border-volt/40 bg-volt/[0.02] relative mb-6"
         data-testid="onboarding-progress-card">
      <button onClick={dismiss} className="absolute top-4 right-4 text-ink-muted hover:text-white"
              title="Dismiss" data-testid="onboarding-dismiss-btn">
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-start gap-3 mb-5">
        <Sparkles className="w-5 h-5 text-volt flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="label-eyebrow text-volt">Get set up · {completedCount} of {steps.length}</div>
          <h2 className="font-heading text-2xl uppercase tracking-tight mt-1">Finish your launch checklist.</h2>
          <p className="text-xs text-ink-muted mt-1.5">Athletes with a complete profile + verified analytics get 3× more brand replies on average.</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-line mb-5 overflow-hidden">
        <div className="h-1 bg-volt transition-all duration-500"
             style={{ width: `${(completedCount / steps.length) * 100}%` }} />
      </div>

      <ul className="space-y-3" data-testid="onboarding-steps">
        {steps.map((s, i) => (
          <li key={s.id} className="flex items-center gap-3" data-testid={`onboarding-step-${s.id}`}>
            <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 border ${
              s.done ? "bg-volt border-volt" : "border-line"
            }`}>
              {s.done ? <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                      : <span className="text-[10px] font-mono text-ink-muted">{i + 1}</span>}
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
              <span className={`text-sm ${s.done ? "text-ink-muted line-through" : "text-white"}`}>{s.label}</span>
              {s.optional && !s.done && (
                <span className="text-[9px] uppercase tracking-widest text-volt border border-volt/40 px-1.5 py-0.5">Optional</span>
              )}
            </div>
            {s.cta && !s.done && (
              <Link to={s.cta.to}
                className="text-xs uppercase tracking-widest text-volt hover:text-white inline-flex items-center gap-1 flex-shrink-0"
                data-testid={`onboarding-cta-${s.id}`}>
                {s.cta.label} <ArrowUpRight className="w-3 h-3" />
              </Link>
            )}
          </li>
        ))}
      </ul>

      {/* Personal-account aspirational nudge */}
      {!hasVerified && hasProfile && (
        <div className="mt-5 pt-5 border-t border-line/60 flex items-start gap-3" data-testid="creator-account-nudge">
          <ShieldCheck className="w-4 h-4 text-volt flex-shrink-0 mt-0.5" />
          <p className="text-xs text-ink-muted leading-relaxed">
            <span className="text-white">Personal Instagram?</span>{" "}
            Switch to a free <a href="https://help.instagram.com/502981923235522" target="_blank" rel="noreferrer" className="text-volt hover:text-white underline">Creator account</a> to unlock the Verified Analytics badge — Brands trust verified data 3× more.
          </p>
        </div>
      )}
    </div>
  );
}
